import { GoogleMap, MarkerF } from '@react-google-maps/api'
import { useRef, useCallback, useState, useEffect } from 'react';

import { mapStyle } from './../helpers/mapStyle'
import { track } from './../helpers/track'

import saintMaur from '../assets/saintMaur.webp'
import chateauroux from '../assets/pic4-chtx.webp'
import markerActive from '../assets/markerActive.svg'
import markerPassive from '../assets/markerPassive.svg'
import medit from '../assets/medit resized.webp'
import useLargeur from '../helpers/useLargeur'

// Chargé À LA DEMANDE (uniquement là où la carte s'affiche) : le script Maps
// était chargé globalement sur toutes les pages via index.html.
const MAPS_LIBRARIES = ['places']
const MAPS_KEY = 'AIzaSyAEpVYQweR671gIgoTQhHD7HzhYWsCmAOk'

// siteUrl : pages ville du site patient des cabinets (oasissante.fr) — preuve
// tangible que les cliniques existent (photos, avis, équipe) + maillage SEO.
const UTM = 'utm_source=neomed&utm_medium=referral&utm_campaign=recrutement'
const CLINICS = [
    {
        key: 0,
        title: "Châteauroux",
        query: "Oasis Santé Châteauroux dentiste",
        address: '16 rue Victor Hugo, 36000 Châteauroux',
        gmapsUrl: 'https://share.google/NqFzHvlY8QYKXPnH2',
        siteUrl: `https://oasissante.fr/dentiste-chateauroux?${UTM}`,
        rooms: '5',
        backgroundImage: chateauroux,
        gps: { lat: 46.81148, lng: 1.69365 }
    },
    {
        key: 1,
        title: "Déols",
        query: "Dentistes Déols Oasis Santé",
        address: '27 rue Ferdinand Gigot, 36130 Déols',
        gmapsUrl: 'https://share.google/Jfnj3HaT5hGBAX5yV',
        siteUrl: `https://oasissante.fr/dentiste-deols?${UTM}`,
        rooms: "3",
        backgroundImage: medit,
        gps: { lat: 46.831237819177936, lng: 1.7006466074184041 }
    },
    {
        key: 2,
        title: "Saint-Maur",
        query: "Cabinet orthodontie Irina Visan Saint-Maur",
        address: 'Pl. de la Mairie, 36250 Saint-Maur',
        gmapsUrl: 'https://share.google/vYEFVlnScFqIwUd03',
        siteUrl: `https://oasissante.fr/orthodontiste-saint-maur?${UTM}`,
        rooms: "3",
        backgroundImage: saintMaur,
        gps: { lat: 46.805649040201686, lng: 1.6394944530561921 }
    },
]

export default function MapLocales(props) {
    const largeur = useLargeur()
    const { isEnglish } = props
    const mapRef = useRef()
    const [scrolledLocale, setScrolledLocale] = useState(0)
    const [currentZoom] = useState(12)
    // Chargement CLASSIQUE du script Maps (URL synchrone) : le mode async de
    // useJsApiLoader ne définit plus google.maps.Point à temps pour MarkerF
    // (crash qui vidait la page à l'hydratation). Lazy quand même : injecté
    // seulement au montage de ce composant — jamais dans le HTML pré-rendu.
    const [showMap, setShowMap] = useState(false)
    // 04/11/2026 : script injecté seulement quand la carte approche de l'écran
    // (elle est en bas de l'accueil), et jamais pendant le snapshot (la balise
    // y était figée SANS async → script bloquant en tête de chaque page).
    const zoneRef = useRef(null)
    const [proche, setProche] = useState(false)
    useEffect(() => {
        const zone = zoneRef.current
        if (!zone || navigator.userAgent.includes('NeomedSnapshot')) return undefined
        if (!('IntersectionObserver' in window)) { setProche(true); return undefined }
        const obs = new IntersectionObserver((e) => { if (e.some(x => x.isIntersecting)) { obs.disconnect(); setProche(true) } }, { rootMargin: '600px' })
        obs.observe(zone)
        return () => obs.disconnect()
    }, [])
    useEffect(() => {
        if (!proche) return undefined
        const ready = () => !!(window.google && window.google.maps && window.google.maps.Point)
        if (ready()) { setShowMap(true); return undefined }
        const done = () => { if (ready()) setShowMap(true) }
        let script = document.getElementById('gmaps-js')
        if (!script) {
            script = document.createElement('script')
            script.id = 'gmaps-js'
            script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=${MAPS_LIBRARIES.join(',')}`
            script.async = true
            document.head.appendChild(script)
        }
        script.addEventListener('load', done)
        return () => script.removeEventListener('load', done)
    }, [proche])
    const [ratings, setRatings] = useState({})

    const locales = CLINICS.map(c => ({
        ...c,
        specialty: c.key === 2
            ? (isEnglish ? 'Orthodontics' : 'Orthodontie')
            : (isEnglish ? 'General Dentistry' : 'Dentisterie'),
    }))

    const fetchRatings = useCallback(() => {
        if (!window.google?.maps?.places || !mapRef.current) return

        const service = new window.google.maps.places.PlacesService(mapRef.current)

        CLINICS.forEach(clinic => {
            service.findPlaceFromQuery(
                {
                    query: clinic.query,
                    fields: ['place_id'],
                    locationBias: { lat: clinic.gps.lat, lng: clinic.gps.lng },
                },
                (results, status) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && results?.[0]?.place_id) {
                        service.getDetails(
                            {
                                placeId: results[0].place_id,
                                fields: ['rating', 'user_ratings_total'],
                            },
                            (place, detailStatus) => {
                                if (detailStatus === window.google.maps.places.PlacesServiceStatus.OK && place) {
                                    setRatings(prev => ({
                                        ...prev,
                                        [clinic.key]: {
                                            rating: place.rating,
                                            count: place.user_ratings_total,
                                        }
                                    }))
                                }
                            }
                        )
                    }
                }
            )
        })
    }, [])

    const options = {
        styles: mapStyle,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: largeur < 500 ? "greedy" : "auto"
    }

    const mapProps = {
        center: { lat: 46.81612, lng: 1.67793 },
        zoom: 12
    };

    const mapContainerStyle = {
        height: '100%',
        width: '100%',
        borderRadius: '12px',
    }

    const onMapLoad = useCallback(map => {
        mapRef.current = map
        setTimeout(fetchRatings, 500)
    }, [fetchRatings])

    return (
        <div className="px-4 sm:px-20 pb-14 sm:pb-32" ref={zoneRef}>
            <div className="bg-white shadow-xl rounded-xl mx-auto mx-10 px-4 sm:px-12 py-12 sm:py-16">
                {/* Header */}
                <div className="mb-8 sm:mb-10 text-center sm:text-left">
                    <h3 className="text-md md:text-xl font-semibold leading-7 text-brand-600">{isEnglish ? "OUR CLINICS" : "NOS CABINETS"}</h3>
                    <h2 className="mb-2 md:mb-3 md:mt-1 text-2xl sm:text-3xl font-bold md:text-4xl tracking-tight text-gray-900">
                        {isEnglish ? "Live and work at the heart of France" : "Vivez et travaillez en coeur de France"}
                    </h2>
                    <p className="max-w-3xl text-base sm:text-lg leading-7 text-gray-800">{isEnglish ? "We currently operate 3 private practices in one of the most attractive regions for dentists." : "Nous opérons actuellement 3 cabinets, dans l'une des régions les plus attractives pour les dentistes."}</p>
                </div>

                {/* Map 1/3 + Cards 2/3 */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 items-stretch">
                    {/* Map */}
                    <div className="lg:col-span-1 rounded-xl overflow-hidden shadow-md" style={{ minHeight: largeur > 1024 ? '0' : '25vh' }}>
                        <div className='googleMaps' style={{ height: '100%' }}>
                            {showMap && <GoogleMap
                                id="map"
                                zoom={mapProps.zoom}
                                center={mapProps.center}
                                styles={mapStyle}
                                mapContainerStyle={mapContainerStyle}
                                options={options}
                                onLoad={onMapLoad}
                            >
                                {locales.map(locale => (
                                    <MarkerF
                                        key={`${locale.gps.lat}-${locale.gps.lng}`}
                                        position={{
                                            lat: locale.gps.lat,
                                            lng: locale.gps.lng,
                                        }}
                                        onClick={() => setScrolledLocale(locale.key)}
                                        icon={{
                                            url: scrolledLocale === locale.key ? markerActive : markerPassive,
                                            origin: new window.google.maps.Point(0, 0),
                                            anchor: new window.google.maps.Point(25, 25),
                                            scaledSize: scrolledLocale === locale.key
                                                ? new window.google.maps.Size(50 * currentZoom / 12, 50 * currentZoom / 12)
                                                : new window.google.maps.Size(40 * currentZoom / 12, 40 * currentZoom / 12),
                                        }}
                                    />
                                ))}
                            </GoogleMap>
                            }
                        </div>
                    </div>

                    {/* Cards */}
                    <div className="lg:col-span-2 flex flex-col gap-3">
                        {locales.map((locale) => {
                            const r = ratings[locale.key]
                            return (
                                <div
                                    key={locale.key}
                                    onClick={() => setScrolledLocale(locale.key)}
                                    className={`group cursor-pointer flex overflow-hidden rounded-xl shadow-md transition-all duration-300 ${scrolledLocale === locale.key
                                        ? 'ring-2 ring-brand-500 scale-[1.02]'
                                        : 'hover:shadow-lg hover:scale-[1.01]'
                                        }`}
                                >
                                    <div className="w-1/4 flex-shrink-0">
                                        <img
                                            src={locale.backgroundImage}
                                            alt={locale.title}
                                            className="h-full w-full object-cover"
                                            style={{ minHeight: '100px' }}
                                        />
                                    </div>
                                    <div className="flex flex-1 flex-col justify-center px-4 py-3 sm:px-5 sm:py-3 bg-white">
                                        <div className="flex items-center justify-between">
                                            <h4 className="text-lg sm:text-xl font-bold text-gray-900">{locale.title}</h4>
                                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${scrolledLocale === locale.key
                                                ? 'bg-brand-50 text-brand-700'
                                                : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                {locale.specialty}
                                            </span>
                                        </div>
                                        <div className="mt-1 flex items-center gap-3">
                                            <p className="text-sm text-gray-500">{locale.rooms} {isEnglish ? 'treatment rooms' : 'salles de soins'}</p>
                                            {r?.rating &&
                                                <a
                                                    href={locale.gmapsUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="flex items-center text-xs font-semibold text-amber-600 hover:text-amber-700"
                                                >
                                                    <span className="text-amber-400 mr-0.5">{'★'.repeat(Math.round(r.rating))}</span>
                                                    {r.rating}{r.count > 0 && <span className="text-gray-400 font-normal ml-1">({r.count})</span>}
                                                </a>
                                            }
                                        </div>
                                        <a
                                            href={locale.gmapsUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="mt-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline"
                                        >
                                            {locale.address} &rarr;
                                        </a>
                                        <a
                                            href={locale.siteUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            onClick={(e) => { e.stopPropagation(); track('oasis_site_click', { city: locale.title }) }}
                                            className="mt-0.5 text-sm font-medium text-gray-500 hover:text-brand-600 hover:underline"
                                        >
                                            {isEnglish ? 'Visit the clinic website' : 'Visiter le site du cabinet'} &rarr;
                                        </a>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>

                {/* CTA */}
                <div className="flex mt-8 sm:mt-10 items-center justify-center gap-x-6">
                    <a
                        href="https://www.youtube.com/watch?v=emRhBjOQaMY&ab_channel=Paris%2Cjetequitte"
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-xl bg-gray-900 text-white px-5 py-3 text-sm font-semibold shadow-sm transition-all duration-200 hover:bg-brand-600 hover:scale-[1.02]"
                    >
                        {isEnglish ? "Discover Châteauroux" : 'Découvrez Châteauroux'}
                    </a>
                    <div onClick={() => props.anchorScroll('#contact-form')} className="text-sm cursor-pointer font-semibold leading-6 text-gray-900 hover:text-brand-600 transition-colors">
                        {isEnglish ? 'Talk to us' : 'Parlez nous'} &rarr;
                    </div>
                </div>
            </div>
        </div>
    )
}
